import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
} from 'react';

// Default pattern matches workflow tokens like {{context:step_0.output}} or
// {{trigger:body}}. Other consumers (e.g. the SQL editor) pass their own
// `tokenPattern` + `getTokenKind` props to introduce additional token
// families without forking this component.
const DEFAULT_TOKEN_REGEX = /\{\{(?:context|trigger):[^{}]+\}\}/g;

function escapeHtml(str) {
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

function defaultTokenLabel(token) {
  const inner = token.slice(2, -2).trim();
  const colonIdx = inner.indexOf(':');
  if (colonIdx === -1) return inner;
  const path = inner.slice(colonIdx + 1);
  const dotIdx = path.indexOf('.');
  return dotIdx === -1 ? path : path.slice(dotIdx + 1);
}

function defaultGetTokenKind(token) {
  return token.startsWith('{{trigger:') ? 'trigger' : 'context';
}

// Clones the caller's regex with the `g` flag so internal `exec()` loops
// always behave correctly, regardless of whether the caller passed a
// global flag or not.
function ensureGlobal(re) {
  return re.flags.includes('g') ? re : new RegExp(re.source, re.flags + 'g');
}

export function buildChipElement(token, label, options = {}) {
  const display = label || defaultTokenLabel(token);
  const kind = options.kind || defaultGetTokenKind(token);
  const span = document.createElement('span');
  span.setAttribute('contenteditable', 'false');
  span.setAttribute('data-token-chip', 'true');
  span.setAttribute('data-token', token);
  span.setAttribute('data-kind', kind);
  span.setAttribute('data-tooltip', token);
  if (label) span.setAttribute('data-label', label);
  span.className = 'token-chip';

  const dot = document.createElement('span');
  dot.className = 'token-chip-dot';
  dot.setAttribute('aria-hidden', 'true');

  const labelSpan = document.createElement('span');
  labelSpan.className = 'token-chip-label';
  labelSpan.textContent = display;

  const remove = document.createElement('button');
  remove.type = 'button';
  remove.tabIndex = -1;
  remove.className = 'token-chip-remove';
  remove.setAttribute('data-action', 'remove');
  remove.setAttribute('aria-label', 'Remove');
  remove.innerHTML =
    '<svg viewBox="0 0 14 14" width="10" height="10" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true">' +
    '<path d="M3 3l8 8M11 3l-8 8"/></svg>';

  span.appendChild(dot);
  span.appendChild(labelSpan);
  span.appendChild(remove);

  if (options.animate !== false) {
    span.classList.add('token-chip-enter');
    span.addEventListener(
      'animationend',
      () => span.classList.remove('token-chip-enter'),
      { once: true }
    );
  }
  return span;
}

function buildChipHtml(token, label, kindOverride) {
  const display = label || defaultTokenLabel(token);
  const kind = kindOverride || defaultGetTokenKind(token);
  const labelAttr = label ? ` data-label="${escapeHtml(label)}"` : '';
  return (
    `<span contenteditable="false" data-token-chip="true" ` +
    `data-token="${escapeHtml(token)}" data-kind="${kind}" ` +
    `data-tooltip="${escapeHtml(token)}"${labelAttr} class="token-chip">` +
    `<span class="token-chip-dot" aria-hidden="true"></span>` +
    `<span class="token-chip-label">${escapeHtml(display)}</span>` +
    `<button type="button" tabindex="-1" class="token-chip-remove" data-action="remove" aria-label="Remove">` +
    `<svg viewBox="0 0 14 14" width="10" height="10" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true">` +
    `<path d="M3 3l8 8M11 3l-8 8"/></svg>` +
    `</button>` +
    `</span>`
  );
}

function valueToHtml(value, multiline, resolveLabel, tokenPattern, getTokenKind) {
  const v = value == null ? '' : String(value);
  if (!v) return '';
  const pattern = ensureGlobal(tokenPattern || DEFAULT_TOKEN_REGEX);
  const kindFn = getTokenKind || defaultGetTokenKind;
  let html = '';
  let last = 0;
  pattern.lastIndex = 0;
  let m;
  while ((m = pattern.exec(v)) !== null) {
    if (m.index > last) {
      html += escapeHtml(v.slice(last, m.index));
    }
    const tok = m[0];
    const lbl = resolveLabel ? resolveLabel(tok) : null;
    html += buildChipHtml(tok, lbl, kindFn(tok));
    last = m.index + tok.length;
  }
  if (last < v.length) {
    html += escapeHtml(v.slice(last));
  }
  if (multiline) {
    html = html.replace(/\n/g, '<br>');
  }
  return html;
}

function nodeToValue(node) {
  if (!node) return '';
  if (node.nodeType === Node.TEXT_NODE) {
    return node.textContent || '';
  }
  if (node.nodeType !== Node.ELEMENT_NODE) return '';
  if (node.dataset && node.dataset.tokenChip === 'true') {
    return node.dataset.token || '';
  }
  if (node.tagName === 'BR') return '\n';
  let result = '';
  if (node.tagName === 'DIV' || node.tagName === 'P') {
    let prefix = '';
    if (node.previousSibling) prefix = '\n';
    result += prefix;
  }
  node.childNodes.forEach((c) => {
    result += nodeToValue(c);
  });
  return result;
}

function rootToValue(root) {
  if (!root) return '';
  let result = '';
  root.childNodes.forEach((c) => {
    result += nodeToValue(c);
  });
  return result;
}

const TokenField = forwardRef(function TokenField(
  {
    value,
    onChange,
    placeholder,
    multiline = false,
    rows,
    className = '',
    inputClassName = '',
    style,
    onFocus,
    onBlur,
    onKeyDown,
    disabled = false,
    resolveLabel,
    tokenPattern,
    getTokenKind,
  },
  ref
) {
  const elRef = useRef(null);
  const composingRef = useRef(false);
  const lastValueRef = useRef('');
  const resolveLabelRef = useRef(resolveLabel);
  const tokenPatternRef = useRef(tokenPattern);
  const getTokenKindRef = useRef(getTokenKind);
  const tooltipElRef = useRef(null);
  const showTimerRef = useRef(null);
  const hideTimerRef = useRef(null);

  useImperativeHandle(ref, () => elRef.current, []);

  useEffect(() => {
    const el = document.createElement('div');
    el.className = 'token-chip-tooltip';
    el.style.opacity = '0';
    document.body.appendChild(el);
    tooltipElRef.current = el;
    return () => {
      clearTimeout(showTimerRef.current);
      clearTimeout(hideTimerRef.current);
      if (document.body.contains(el)) document.body.removeChild(el);
    };
  }, []);

  useEffect(() => {
    resolveLabelRef.current = resolveLabel;
    tokenPatternRef.current = tokenPattern;
    getTokenKindRef.current = getTokenKind;
  }, [resolveLabel, tokenPattern, getTokenKind]);

  useEffect(() => {
    const el = elRef.current;
    if (!el) return;
    const incoming = value == null ? '' : String(value);
    if (incoming === lastValueRef.current) return;
    el.innerHTML = valueToHtml(
      incoming,
      multiline,
      resolveLabelRef.current,
      tokenPatternRef.current,
      getTokenKindRef.current
    );
    lastValueRef.current = incoming;
  }, [value, multiline]);

  const fireChange = () => {
    if (composingRef.current) return;
    const el = elRef.current;
    if (!el) return;
    const v = rootToValue(el);
    lastValueRef.current = v;
    el.setAttribute('data-empty', v ? 'false' : 'true');
    onChange?.(v);
  };

  const handleClick = (e) => {
    const removeBtn = e.target.closest && e.target.closest('[data-action="remove"]');
    if (removeBtn) {
      e.preventDefault();
      e.stopPropagation();
      const chip = removeBtn.closest('[data-token-chip]');
      if (chip && chip.parentNode) {
        const parent = chip.parentNode;
        chip.classList.add('token-chip-leaving');
        const finish = () => {
          if (parent.contains(chip)) parent.removeChild(chip);
          fireChange();
        };
        let done = false;
        const cleanup = () => {
          if (done) return;
          done = true;
          finish();
        };
        chip.addEventListener('transitionend', cleanup, { once: true });
        setTimeout(cleanup, 200);
      }
      elRef.current?.focus();
    }
  };

  const handleKeyDown = (e) => {
    // Let the parent intercept first — used by callers like SqlEditor
    // to wire Cmd/Ctrl+Enter to a Run action.
    onKeyDown?.(e);
    if (e.defaultPrevented) return;
    if (!multiline && e.key === 'Enter') {
      e.preventDefault();
      return;
    }
    if (e.key === 'Backspace') {
      const sel = window.getSelection();
      if (sel && sel.isCollapsed && sel.rangeCount > 0) {
        const range = sel.getRangeAt(0);
        const { startContainer, startOffset } = range;
        let prev = null;
        if (startContainer.nodeType === Node.TEXT_NODE && startOffset === 0) {
          prev = startContainer.previousSibling;
        } else if (startContainer.nodeType === Node.ELEMENT_NODE) {
          prev = startContainer.childNodes[startOffset - 1] || null;
        }
        if (
          prev &&
          prev.nodeType === Node.ELEMENT_NODE &&
          prev.dataset &&
          prev.dataset.tokenChip === 'true'
        ) {
          e.preventDefault();
          const chip = prev;
          const parent = chip.parentNode;
          chip.classList.add('token-chip-leaving');
          let done = false;
          const finish = () => {
            if (done) return;
            done = true;
            if (parent.contains(chip)) parent.removeChild(chip);
            fireChange();
          };
          chip.addEventListener('transitionend', finish, { once: true });
          setTimeout(finish, 200);
        }
      }
    }
  };

  const handleMouseOver = (e) => {
    const chip = e.target.closest && e.target.closest('[data-token-chip="true"]');
    if (!chip || !tooltipElRef.current) return;
    const text = chip.getAttribute('data-tooltip');
    if (!text) return;
    clearTimeout(hideTimerRef.current);
    showTimerRef.current = setTimeout(() => {
      const tooltip = tooltipElRef.current;
      if (!tooltip) return;
      const rect = chip.getBoundingClientRect();
      tooltip.textContent = text;
      tooltip.style.left = `${rect.left + rect.width / 2}px`;
      tooltip.style.top = `${rect.top - 8}px`;
      tooltip.style.transform = 'translateX(-50%) translateY(-100%)';
      tooltip.style.opacity = '1';
    }, 200);
  };

  const handleMouseLeave = (e) => {
    clearTimeout(showTimerRef.current);
    hideTimerRef.current = setTimeout(() => {
      if (tooltipElRef.current) tooltipElRef.current.style.opacity = '0';
    }, 80);
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const text = (e.clipboardData || window.clipboardData).getData('text');
    if (!text) return;
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0) return;
    const range = sel.getRangeAt(0);
    range.deleteContents();

    const fragment = document.createDocumentFragment();
    let last = 0;
    const pastePattern = ensureGlobal(
      tokenPatternRef.current || DEFAULT_TOKEN_REGEX
    );
    pastePattern.lastIndex = 0;
    let m;
    const resolver = resolveLabelRef.current;
    const kindFn = getTokenKindRef.current || defaultGetTokenKind;
    while ((m = pastePattern.exec(text)) !== null) {
      if (m.index > last) {
        fragment.appendChild(document.createTextNode(text.slice(last, m.index)));
      }
      const tok = m[0];
      const lbl = resolver ? resolver(tok) : null;
      fragment.appendChild(buildChipElement(tok, lbl, { kind: kindFn(tok) }));
      last = m.index + tok.length;
    }
    if (last < text.length) {
      const tail = text.slice(last);
      if (multiline) {
        const lines = tail.split('\n');
        lines.forEach((line, i) => {
          if (i > 0) fragment.appendChild(document.createElement('br'));
          if (line) fragment.appendChild(document.createTextNode(line));
        });
      } else {
        fragment.appendChild(document.createTextNode(tail.replace(/\n/g, ' ')));
      }
    }

    const lastChild = fragment.lastChild;
    range.insertNode(fragment);
    if (lastChild) {
      const after = document.createRange();
      after.setStartAfter(lastChild);
      after.collapse(true);
      sel.removeAllRanges();
      sel.addRange(after);
    }
    fireChange();
  };

  const minHeight = rows ? `${rows * 1.4}em` : undefined;

  return (
    <div
      ref={elRef}
      contentEditable={!disabled}
      suppressContentEditableWarning
      data-tokenfield="true"
      data-multiline={multiline ? 'true' : 'false'}
      data-empty={!value ? 'true' : 'false'}
      role="textbox"
      aria-multiline={multiline}
      aria-disabled={disabled}
      data-placeholder={placeholder || ''}
      className={`token-field ${className} ${inputClassName}`}
      style={{
        minHeight,
        whiteSpace: multiline ? 'pre-wrap' : 'nowrap',
        overflowX: multiline ? 'hidden' : 'auto',
        overflowY: multiline ? 'auto' : 'hidden',
        wordBreak: 'break-word',
        ...style,
      }}
      onInput={fireChange}
      onCompositionStart={() => {
        composingRef.current = true;
      }}
      onCompositionEnd={() => {
        composingRef.current = false;
        fireChange();
      }}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      onPaste={handlePaste}
      onMouseOver={handleMouseOver}
      onMouseLeave={handleMouseLeave}
      onFocus={onFocus}
      onBlur={onBlur}
    />
  );
});

export default TokenField;
